import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { CheckCircle2, ChevronDown, ChevronRight, Download, FileText, Inbox, Layers, Plus, Search, Upload, X, XCircle } from 'lucide-react';
import { Button, CenteredSpinner, Chip, EmptyState, ErrorState } from '@/components/ui';
import { cn } from '@/lib/format';
import { t } from '@/lib/i18n';
import {
  useArtwork,
  useArtworkBrands,
  useArtworkGroup,
  useArtworkIngest,
  useArtworkStatus,
  useArtworkUpload,
  useInvalidateArtwork,
} from '@/lib/queries';
import { api, type ArtworkGroup, type ArtworkUploadFields } from '@/lib/api';

/**
 * Artwork index — the ArtWorkIndexBundle screen.
 *
 * The business searches by product code (PWS / JWS / SAP) to find a product's
 * master artwork PDF and every source file linked to it (.ai/.psd/.eps). That
 * lookup is the whole point of the page, so the code filters are first-class
 * inputs rather than buried in an "advanced" panel.
 *
 * Two things about the underlying data shape the UI:
 *
 *  - It is a manifest (DAM_INDEX.json) that changes only when someone adds
 *    artwork, through the "Add artwork PDF" form or the _INBOX folder, so there
 *    is nothing to poll. When it last changed is stated once in the footer.
 *  - A linked file often has **no asset id** — either it was never indexed into
 *    Pimcore (~2% of prod's index) or the caller lacks permission on it. Those
 *    rows still appear, as plain text without a link, because the file is real
 *    and people search for it by name. Hiding them would misrepresent what the
 *    artwork consists of.
 */

const LIMIT = 60;

/** Source-file badge colours, so .ai/.psd/.eps are distinguishable at a glance. */
const EXT_TONE: Record<string, string> = {
  ai: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  psd: 'bg-sky-500/15 text-sky-700 dark:text-sky-400',
  eps: 'bg-violet-500/15 text-violet-700 dark:text-violet-400',
  indd: 'bg-pink-500/15 text-pink-700 dark:text-pink-400',
  pdf: 'bg-red-500/15 text-red-700 dark:text-red-400',
};

function extTone(ext: string | null) {
  return EXT_TONE[(ext ?? '').toLowerCase()] ?? 'bg-black/5 text-muted dark:bg-white/10';
}

/** Brands are stored with underscores ('Allos_Tea'); people read them with spaces. */
function brandLabel(b: string) {
  return b.replace(/_/g, ' ');
}

function sizeLabel(mb: number | null) {
  if (mb === null) return null;
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(mb * 1024)} KB`;
}

export function ArtworkPage() {
  // The filters are URL state, so a lookup is linkable and — the reason this
  // changed — survives opening one of its files and coming back. `replace`
  // throughout: refining a search is one destination, not a history trail to
  // step back through a character at a time.
  const navigate = useNavigate();
  const search = useSearch({ from: '/shell/artwork' });
  const brand = search.brand;
  const open = search.open ?? null;

  const setParams = (next: Partial<typeof search>) =>
    void navigate({ to: '/artwork', search: (prev) => ({ ...prev, ...next }), replace: true });

  // Text boxes keep local state so typing stays responsive, and sync to the URL
  // on a short debounce rather than per keystroke.
  const [q, setQ] = useState(search.q ?? '');
  const [sap, setSap] = useState(search.sap ?? '');
  const [job, setJob] = useState(search.job ?? '');

  // Adopt values that arrived from the URL rather than the keyboard — a pasted
  // link, or Back from an asset page restoring the search that was running.
  useEffect(() => { setQ(search.q ?? ''); }, [search.q]);
  useEffect(() => { setSap(search.sap ?? ''); }, [search.sap]);
  useEffect(() => { setJob(search.job ?? ''); }, [search.job]);

  useEffect(() => {
    const next = {
      q: q.trim() || undefined,
      sap: sap.trim() || undefined,
      job: job.trim() || undefined,
    };
    // Nothing to write when the boxes already agree with the URL. Without this
    // the effect fires on arrival and immediately re-navigates, which would
    // fight the Back that just restored these values.
    if (next.q === search.q && next.sap === search.sap && next.job === search.job) return;
    const t = setTimeout(() => setParams(next), 300);
    return () => clearTimeout(t);
  }, [q, sap, job, search.q, search.sap, search.job]);

  const filter = {
    q: search.q,
    brand,
    sap: search.sap,
    job: search.job,
    limit: LIMIT,
  };
  const { data: items, isPending, error, refetch } = useArtwork(filter);
  const { data: brands } = useArtworkBrands();
  const { data: status } = useArtworkStatus();

  // The whole page takes dropped PDFs, so adding artwork needs no clicks: the
  // files go to the bulk drop, which opens to show its progress.
  const [dropped, setDropped] = useState<File[] | null>(null);
  const [dragging, setDragging] = useState(false);
  const isFileDrag = (e: React.DragEvent) => Boolean(status?.canUpload) && e.dataTransfer.types.includes('Files');

  const hasFilters = Boolean(filter.q || brand || filter.sap || filter.job);
  const clear = () => {
    setQ(''); setSap(''); setJob('');
    setParams({ q: undefined, brand: undefined, sap: undefined, job: undefined, open: undefined });
  };

  // The backend has no artwork index at all (native mode). Say so plainly
  // rather than rendering an empty list that looks like a data problem.
  if (status && !status.present) {
    return (
      <div className="scroll-thin h-full overflow-y-auto">
        <div className="w-full px-6 py-6">
          <EmptyState
            icon={<Layers />}
            title={t('artworkPage.noArtworkIndex')}
            description={t('artworkPage.noIndexDescription')}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className="scroll-thin relative h-full overflow-y-auto"
      onDragOver={(e) => {
        if (!isFileDrag(e)) return;
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false);
      }}
      onDrop={(e) => {
        if (!isFileDrag(e)) return;
        e.preventDefault();
        setDragging(false);
        setDropped([...e.dataTransfer.files]);
      }}
    >
      {dragging && (
        <div className="pointer-events-none fixed inset-0 z-40 flex items-center justify-center bg-brand-500/10 backdrop-blur-[1px]">
          <div className="flex items-center gap-2 rounded-xl border-2 border-dashed border-brand-500 surface-card px-6 py-4 text-sm font-medium text-strong shadow-lg">
            <Upload className="size-5 text-brand-600" /> {t('artworkPage.dropToAdd')}
          </div>
        </div>
      )}
      <div className="w-full px-6 py-6">
        <div className="mb-4 flex items-center gap-2">
          <Layers className="size-5 text-brand-600 dark:text-brand-400" />
          <h1 className="text-lg font-semibold">{t('artworkPage.artworkIndex')}</h1>
          {status?.records ? (
            <Chip>{t('artworkPage.nProducts', { n: status.records.toLocaleString() })}</Chip>
          ) : null}
        </div>

        {status?.canUpload && (
          <AddArtwork brands={brands ?? []} dropped={dropped} onDropHandled={() => setDropped(null)} />
        )}

        {/* Search + code filters. Code lookups are the primary path, so they sit
            beside the text box rather than behind a disclosure. Styling follows
            the search page's per-column filters — design tokens rather than raw
            alpha-black, so the fields keep their edges against the page wash and
            placeholders stay legible. */}
        <div className="mb-4 space-y-2">
          <label className="relative block">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-faint" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('artworkPage.searchPlaceholder')}
              aria-label={t('artworkPage.searchArtwork')}
              className="w-full rounded-lg border line surface-card py-2 pl-9 pr-3 text-sm text-strong placeholder:text-muted focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </label>

          <div className="flex flex-wrap items-end gap-2">
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-muted">{t('artworkPage.jobCode')}</span>
              <input
                value={job}
                onChange={(e) => setJob(e.target.value)}
                placeholder={t('artworkPage.pwsJwsCode')}
                className="w-44 rounded-lg border line surface-card px-3 py-1.5 text-sm text-strong placeholder:text-muted focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-muted">{t('artworkPage.sapCode')}</span>
              <input
                value={sap}
                onChange={(e) => setSap(e.target.value)}
                placeholder={t('artworkPage.sapCode')}
                className="w-36 rounded-lg border line surface-card px-3 py-1.5 text-sm text-strong placeholder:text-muted focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-muted">{t('artworkPage.brand')}</span>
              <select
                value={brand ?? ''}
                onChange={(e) => setParams({ brand: e.target.value || undefined })}
                className="rounded-lg border line surface-card px-3 py-1.5 text-sm text-strong focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              >
                <option value="">{t('artworkPage.allBrands')}</option>
                {(brands ?? []).map((b) => (
                  <option key={b} value={b}>{brandLabel(b)}</option>
                ))}
              </select>
            </label>
            {hasFilters && (
              <button
                onClick={clear}
                className="flex items-center gap-1 rounded-lg px-2 py-1.5 text-sm text-muted hover:surface-hover"
              >
                <X className="size-3.5" /> {t('artworkPage.clear')}
              </button>
            )}
          </div>
        </div>

        {isPending ? (
          <CenteredSpinner />
        ) : error ? (
          <ErrorState error={error} onRetry={() => refetch()} />
        ) : !items || items.length === 0 ? (
          <EmptyState
            icon={<Search />}
            title={t('artworkPage.noArtworkFound')}
            description={hasFilters ? t('artworkPage.noMatchFilters') : t('artworkPage.indexEmpty')}
          />
        ) : (
          <>
            {/* Two columns on wide screens; an opened row spans both so its
                preview and file list have room. */}
            <ul className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              {items.map((g) => (
                <ArtworkRow
                  key={g.pdfKey}
                  group={g}
                  open={open === g.pdfKey}
                  onToggle={() => setParams({ open: open === g.pdfKey ? undefined : g.pdfKey })}
                />
              ))}
            </ul>

            {/* The API caps results; saying so beats implying this is everything. */}
            {items.length >= LIMIT && (
              <p className="mt-3 text-center text-xs text-muted">
                {t('artworkPage.showingFirst', { limit: LIMIT })}
              </p>
            )}
          </>
        )}

        {status?.generatedAt && (
          <p className="mt-6 text-center text-xs text-muted">
            {t('artworkPage.indexUpdated', { date: new Date(status.generatedAt).toLocaleDateString(), files: status.linkedFiles.toLocaleString() })}
          </p>
        )}
      </div>
    </div>
  );
}

const FIELD =
  'w-full rounded-lg border line surface-card px-3 py-1.5 text-sm text-strong placeholder:text-muted focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';

const EMPTY_FIELDS: ArtworkUploadFields = { brand: '', pws: '', jws: '', version: '', sap: '', description: '' };

/**
 * Adding artwork, the same two ways the old DAM offers: one PDF through the
 * form (the fields make up the standard filename), or many at once by dropping
 * correctly named PDFs into the _INBOX folder and processing it.
 */
function AddArtwork({
  brands,
  dropped,
  onDropHandled,
}: {
  brands: string[];
  dropped: File[] | null;
  onDropHandled: () => void;
}) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (dropped) setOpen(true);
  }, [dropped]);
  const [fields, setFields] = useState<ArtworkUploadFields>(EMPTY_FIELDS);
  const [pdf, setPdf] = useState<File | null>(null);
  const [fileKey, setFileKey] = useState(0);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [confirmReplace, setConfirmReplace] = useState(false);
  const upload = useArtworkUpload();
  const ingest = useArtworkIngest();

  const set = (k: keyof ArtworkUploadFields) => (e: { target: { value: string } }) => {
    setFields((f) => ({ ...f, [k]: e.target.value }));
    setConfirmReplace(false);
  };

  const submit = async (replace: boolean) => {
    setNote(null);
    if (!fields.brand || !fields.pws.trim() || !fields.description.trim() || !pdf) {
      setNote({ ok: false, text: t('artworkPage.requiredFields') });
      return;
    }
    try {
      const res = await upload.mutateAsync({ fields, pdf, replace });
      if (res.success) {
        setNote({ ok: true, text: `${res.replaced ? t('artworkPage.replaced') : t('artworkPage.added')} ${res.filename}` });
        setFields((f) => ({ ...EMPTY_FIELDS, brand: f.brand }));
        setPdf(null);
        setFileKey((k) => k + 1);
        setConfirmReplace(false);
      } else if (res.error === 'exists') {
        setConfirmReplace(true);
        setNote({ ok: false, text: t('artworkPage.fileExists') });
      } else {
        setNote({ ok: false, text: res.error ?? t('artworkPage.uploadFailed') });
      }
    } catch (e) {
      setNote({ ok: false, text: e instanceof Error ? e.message : t('artworkPage.uploadFailed') });
    }
  };

  const runIngest = async () => {
    setNote(null);
    try {
      const res = await ingest.mutateAsync();
      const lines = [t('artworkPage.inboxSummary', { added: res.added.length, rejected: res.rejected.length })];
      for (const r of res.rejected) lines.push(`${r.filename}: ${r.reason}`);
      setNote({ ok: res.rejected.length === 0, text: lines.join('\n') });
    } catch (e) {
      setNote({ ok: false, text: e instanceof Error ? e.message : t('artworkPage.inboxFailed') });
    }
  };

  return (
    <div className="mb-4 rounded-lg border line surface-card">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium text-strong"
        aria-expanded={open}
      >
        {open ? <ChevronDown className="size-4 text-muted" /> : <ChevronRight className="size-4 text-muted" />}
        <Plus className="size-4 text-brand-600 dark:text-brand-400" />
        {t('artworkPage.addArtworkPdfs')}
      </button>

      {open && (
        <div className="space-y-3 border-t line px-3 py-3">
          <BulkDrop brands={brands} dropped={dropped} onDropHandled={onDropHandled} />

          <p className="border-t line pt-3 text-xs font-semibold uppercase tracking-wide text-muted">
            {t('artworkPage.orOnePdf')}
          </p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-muted">{t('artworkPage.brand')}</span>
              <select value={fields.brand} onChange={set('brand')} className={FIELD}>
                <option value="">{t('artworkPage.choose')}</option>
                {brands.map((b) => (
                  <option key={b} value={b}>{brandLabel(b)}</option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-muted">{t('artworkPage.pws')}</span>
              <input value={fields.pws} onChange={set('pws')} placeholder={t('artworkPage.pwsExample')} className={FIELD} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-muted">{t('artworkPage.jws')}</span>
              <input value={fields.jws} onChange={set('jws')} placeholder={t('artworkPage.jwsExample')} className={FIELD} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-muted">{t('artworkPage.version')}</span>
              <input value={fields.version} onChange={set('version')} placeholder={t('artworkPage.versionExample')} className={FIELD} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-muted">{t('artworkPage.sapCode')}</span>
              <input value={fields.sap} onChange={set('sap')} placeholder={t('artworkPage.sapExample')} className={FIELD} />
            </label>
            <label className="flex flex-col gap-1 lg:col-span-2">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-muted">{t('artworkPage.description')}</span>
              <input value={fields.description} onChange={set('description')} placeholder={t('artworkPage.descriptionExample')} className={FIELD} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-muted">{t('artworkPage.pdf')}</span>
              <input
                key={fileKey}
                type="file"
                accept="application/pdf,.pdf"
                onChange={(e) => { setPdf(e.target.files?.[0] ?? null); setConfirmReplace(false); }}
                className="text-sm text-body file:mr-2 file:rounded-md file:border file:border-solid file:px-2 file:py-1 file:text-sm"
              />
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {confirmReplace ? (
              <>
                <Button variant="danger" loading={upload.isPending} onClick={() => void submit(true)}>{t('artworkPage.replaceIt')}</Button>
                <Button variant="ghost" onClick={() => { setConfirmReplace(false); setNote(null); }}>{t('artworkPage.cancel')}</Button>
              </>
            ) : (
              <Button variant="primary" loading={upload.isPending} onClick={() => void submit(false)}>{t('artworkPage.upload')}</Button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 border-t line pt-3 text-xs text-muted">
            <span className="min-w-0 flex-1">
              {t('artworkPage.inboxHintBefore')}<code>/INTERNAL/ARTWORK_PDFS/_INBOX</code>{t('artworkPage.inboxHintAfter')}
            </span>
            <Button loading={ingest.isPending} onClick={() => void runIngest()}>
              <Inbox className="size-4" /> {t('artworkPage.processInbox')}
            </Button>
          </div>

          {note && (
            <p
              role="status"
              className={cn('whitespace-pre-line text-sm', note.ok ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400')}
            >
              {note.text}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/** Files per request, and bytes per request: the index is rewritten once per batch. */
const BATCH_FILES = 10;
const BATCH_BYTES = 150 * 1024 * 1024;

type BulkLine = { name: string; ok: boolean; text: string };

/**
 * Drop any number of PDFs, named any way. The server reads brand, PWS, JWS,
 * version and SAP from each name, files it under the standard name and skips
 * (never overwrites) what it cannot file, saying why. Files go up in small
 * batches so results appear as it works and one bad batch does not stop the rest.
 */
function BulkDrop({
  brands,
  dropped,
  onDropHandled,
}: {
  brands: string[];
  dropped: File[] | null;
  onDropHandled: () => void;
}) {
  const [brand, setBrand] = useState('');
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState<{ done: number; total: number } | null>(null);
  const [lines, setLines] = useState<BulkLine[]>([]);
  const input = useRef<HTMLInputElement>(null);
  const invalidate = useInvalidateArtwork();

  const run = async (picked: File[]) => {
    if (busy || picked.length === 0) return;
    const batches: File[][] = [];
    let cur: File[] = [];
    let bytes = 0;
    for (const f of picked) {
      if (cur.length && (cur.length >= BATCH_FILES || bytes + f.size > BATCH_BYTES)) {
        batches.push(cur);
        cur = [];
        bytes = 0;
      }
      cur.push(f);
      bytes += f.size;
    }
    if (cur.length) batches.push(cur);

    setLines([]);
    let done = 0;
    let filedAny = false;
    setBusy({ done, total: picked.length });
    for (const batch of batches) {
      try {
        const res = await api.artworkBulk(batch, brand);
        filedAny ||= res.added.length > 0;
        setLines((l) => [
          ...l,
          ...res.added.map((a) => ({ name: a.filename, ok: true, text: t('artworkPage.addedAs', { filed: a.filed }) })),
          ...res.rejected.map((r) => ({ name: r.filename, ok: false, text: r.reason })),
        ]);
      } catch (e) {
        const reason = e instanceof Error ? e.message : t('artworkPage.uploadFailed');
        setLines((l) => [...l, ...batch.map((f) => ({ name: f.name, ok: false, text: reason }))]);
      }
      done += batch.length;
      setBusy({ done, total: picked.length });
    }
    setBusy(null);
    if (filedAny) invalidate();
  };

  // Files dropped anywhere on the page arrive here.
  useEffect(() => {
    if (!dropped) return;
    onDropHandled();
    void run(dropped);
  }, [dropped]);

  const added = lines.filter((l) => l.ok).length;

  return (
    <div className="space-y-2">
      <div
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOver(false);
          void run([...e.dataTransfer.files]);
        }}
        onClick={() => input.current?.click()}
        className={cn(
          'flex cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed px-4 py-8 text-center transition-colors',
          over ? 'border-brand-500 bg-brand-500/5' : 'line hover:border-brand-500/60',
        )}
      >
        <Upload className="size-6 text-brand-600 dark:text-brand-400" />
        <span className="text-sm font-medium text-strong">
          {busy ? t('artworkPage.addingNOfM', { done: busy.done, total: busy.total }) : t('artworkPage.dropHereOrClick')}
        </span>
        <span className="text-xs text-muted">
          {t('artworkPage.dropHint')}
        </span>
        <input
          ref={input}
          type="file"
          accept="application/pdf,.pdf"
          multiple
          hidden
          onChange={(e) => {
            void run([...(e.target.files ?? [])]);
            e.target.value = '';
          }}
        />
      </div>

      <label className="flex flex-wrap items-center gap-2 text-xs text-muted">
        {t('artworkPage.brandForUnnamed')}
        <select
          value={brand}
          onChange={(e) => setBrand(e.target.value)}
          className="rounded-lg border line surface-card px-2 py-1 text-sm text-strong focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        >
          <option value="">{t('artworkPage.noneSkipThem')}</option>
          {brands.map((b) => (
            <option key={b} value={b}>{brandLabel(b)}</option>
          ))}
        </select>
      </label>

      {lines.length > 0 && (
        <div className="rounded-lg border line">
          <p className="border-b line px-3 py-1.5 text-xs font-medium text-strong">
            {t('artworkPage.addedSkipped', { added, skipped: lines.length - added })}
          </p>
          <ul className="scroll-thin max-h-64 overflow-y-auto">
            {lines.map((l, i) => (
              <li key={`${l.name}-${i}`} className="flex items-start gap-2 px-3 py-1 text-xs">
                {l.ok ? (
                  <CheckCircle2 className="mt-px size-3.5 shrink-0 text-emerald-600" />
                ) : (
                  <XCircle className="mt-px size-3.5 shrink-0 text-red-600" />
                )}
                <span className="min-w-0 flex-1 truncate text-strong" title={l.name}>{l.name}</span>
                <span className={cn('min-w-0 flex-1 truncate', l.ok ? 'text-muted' : 'text-red-700 dark:text-red-400')} title={l.text}>
                  {l.text}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * One product row. Collapsed it shows the codes people scan for; expanded it
 * fetches the linked files, which is the only request that resolves asset ids.
 */
function ArtworkRow({
  group,
  open,
  onToggle,
}: {
  group: ArtworkGroup;
  open: boolean;
  onToggle: () => void;
}) {
  // Only fetch the detail once opened — resolving ids is a per-group query.
  const { data: detail, isPending } = useArtworkGroup(open ? group.pdfKey : undefined);
  const shown = detail ?? group;

  return (
    <li
      className={cn(
        'overflow-hidden rounded-xl border border-[var(--line-strong)] surface-card shadow-sm transition-shadow hover:shadow-md',
        open && 'lg:col-span-2',
      )}
    >
      {/* The codes are the thing people copy out of this page, so they sit
          *outside* the toggle button — inside it, a drag to select would be
          swallowed as a click and collapse the row. Only the chevron and title
          toggle; the chips below are ordinary selectable text. */}
      <div className="flex items-start gap-3 px-3 py-2.5">
        <ArtworkThumb url={group.pdfThumbUrl ?? null} className="h-14 w-16" />
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-3">
            <button
              onClick={onToggle}
              className="-m-1 flex min-w-0 flex-1 items-start gap-3 rounded p-1 text-left hover:bg-black/[0.02] dark:hover:bg-white/5"
              aria-expanded={open}
            >
              {open ? (
                <ChevronDown className="mt-0.5 size-4 shrink-0 text-muted" />
              ) : (
                <ChevronRight className="mt-0.5 size-4 shrink-0 text-muted" />
              )}
              <span className="min-w-0 flex-1 truncate text-sm font-medium">
                {group.productDescription || group.pdfFilename || group.pdfKey}
              </span>
            </button>

            <span className="shrink-0 whitespace-nowrap text-xs text-muted">
              {t(group.linkedFiles.length === 1 ? 'artworkPage.oneFile' : 'artworkPage.nFiles', { n: group.linkedFiles.length })}
            </span>
          </div>

          <div className="mt-1.5 flex flex-wrap items-center gap-1.5 pl-7 text-xs">
            {group.brand && <Chip selectable>{brandLabel(group.brand)}</Chip>}
            {group.pwsCode && <Chip selectable>{group.pwsCode}</Chip>}
            {group.jwsCode && <Chip selectable>{group.jwsCode}</Chip>}
            {/* Sentinel SAP values are normalised to null upstream, so a missing
                code genuinely means "no SAP code" rather than "not loaded". */}
            {group.sapCode ? <Chip selectable>{t('artworkPage.sapCodeValue', { code: group.sapCode })}</Chip> : null}
            {group.version && <span className="select-text text-muted">{group.version}</span>}
          </div>
        </div>
      </div>

      {open && (
        <div className="border-t line px-3 py-2.5">
          {isPending ? (
            <p className="py-2 text-center text-xs text-muted">{t('artworkPage.loadingFiles')}</p>
          ) : (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
              {shown.pdfThumbUrl && (
                <a
                  href={shown.pdfUrl ?? undefined}
                  target="_blank"
                  rel="noreferrer"
                  title={t('artworkPage.openThePdf')}
                  className="shrink-0 self-start"
                >
                  <ArtworkThumb url={shown.pdfThumbUrl} className="h-64 w-48" />
                </a>
              )}
              <div className="min-w-0 flex-1">
                <FileRow
                  name={shown.pdfFilename}
                  ext="pdf"
                  sizeMb={shown.sizeMb}
                  url={shown.pdfUrl ?? null}
                  assetId={shown.pdfAssetId}
                  primary
                />
                {shown.linkedFiles.map((l, i) => (
                  <FileRow
                    key={`${l.path ?? l.filename ?? i}`}
                    name={l.filename}
                    ext={l.extension}
                    sizeMb={l.sizeMb}
                    url={l.url ?? null}
                    assetId={l.assetId}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

/**
 * First page of the artwork PDF, rendered by the thumbnail endpoint (cached
 * after the first request). Lazy, so only rows on screen ask for one; a PDF
 * with no asset, or one that fails to render, shows a plain file icon.
 */
function ArtworkThumb({ url, className }: { url: string | null; className?: string }) {
  const [failed, setFailed] = useState(false);
  return (
    <div
      className={cn(
        'flex shrink-0 items-center justify-center overflow-hidden rounded-md border line bg-white dark:bg-white/90',
        className,
      )}
    >
      {url && !failed ? (
        <img src={url} alt="" loading="lazy" onError={() => setFailed(true)} className="size-full object-contain" />
      ) : (
        <FileText className="size-5 text-faint" />
      )}
    </div>
  );
}

/**
 * A single file.
 *
 * The filename links to the asset's page in the DAM (`/assets/:id`), not to the
 * raw bytes: people looking up artwork want the place the file lives — preview,
 * metadata, download options, folder context — rather than an immediate
 * download. The raw URL is still offered alongside as an explicit "Download",
 * which is the only thing the older build did.
 *
 * `assetId` is null when the file has no asset page — never indexed (~2% of
 * prod's frozen index), or not permitted — in which case the name renders as
 * plain text. The row is still shown: the file exists and is part of the
 * artwork.
 */
function FileRow({
  name,
  ext,
  sizeMb,
  url,
  assetId,
  primary = false,
}: {
  name: string | null;
  ext: string | null;
  sizeMb: number | null;
  url: string | null;
  assetId: number | null;
  primary?: boolean;
}) {
  if (!name) return null;
  const size = sizeLabel(sizeMb);

  return (
    <div className="group flex items-center gap-2 py-1">
      <span className={cn('rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase', extTone(ext))}>
        {ext ?? '?'}
      </span>
      {assetId !== null ? (
        <Link
          to="/assets/$assetId"
          params={{ assetId }}
          className={cn(
            'min-w-0 flex-1 truncate text-sm text-brand-700 hover:underline dark:text-brand-400',
            primary && 'font-medium',
          )}
        >
          {name}
        </Link>
      ) : (
        <span
          className={cn('min-w-0 flex-1 truncate text-sm text-muted', primary && 'font-medium')}
          title={t('artworkPage.notInDam')}
        >
          {name}
        </span>
      )}
      {primary && <FileText className="size-3.5 shrink-0 text-muted" />}
      {size && <span className="shrink-0 text-xs text-muted">{size}</span>}
      {/* Secondary: straight to the bytes, for people who just want the file. */}
      {url && (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          title={t('artworkPage.downloadName', { name })}
          className="shrink-0 rounded p-1 text-muted opacity-0 transition-opacity hover:text-strong focus-visible:opacity-100 group-hover:opacity-100"
        >
          <Download className="size-3.5" />
        </a>
      )}
    </div>
  );
}
